// MarketingHomePage.jsx
//
// The public landing page shown to unauthenticated visitors at #/.
// Structure (top → bottom):
//
//   1. Sticky nav (rendered by AppWrapper, not here)
//   2. Hero — headline + sub + two CTAs + product screenshot
//   3. Trust strip — "Built for Ghana", "Bank security", "GHS pricing"
//   4. Features — 4 alternating panels with icons + mock product UI
//   5. Workflow strip — "From intake to invoice in one place"
//   6. Pricing teaser — two-card preview with "See full pricing" CTA
//   7. Testimonial / quote card (single, intentionally lightweight)
//   8. Big CTA — "Book a 20-minute demo"
//   9. Footer (rendered by AppWrapper)
//
// All sections use the BRAND tokens from marketingTokens.js so the
// blue/orange identity stays consistent with LoginPage.jsx.

import React from 'react';
import {
  ArrowRight, Check, Shield, Clock, Users, FileText, MessageSquare,
  Scale, DollarSign, Briefcase, Lock, Globe2,
  Sparkles, Gavel, BookOpen, Paperclip, ShieldCheck,
  Folder, FileLock2, FilePlus, ChevronRight as ChevRight,
  Receipt, AlertCircle, Hash, Send, AtSign,
} from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';
import MarketingHeroDemo from './MarketingHeroDemo';
import { WatchDemoButton } from './WatchDemoModal';
import { PLANS as PLAN_META } from '../../lib/plans';

// ─── Static content ─────────────────────────────────────────────────
// All hand-written copy lives here so editing is a one-stop shop.

const HERO = {
  eyebrow: 'Built for Ghanaian law firms',
  headline: 'Practice law, not paperwork.',
  sub: (
    <>
      DocketFlow is the complete practice-management platform for firms in Ghana —
      case files, time, billing, trust accounting, secure messaging, and client portals.
      All priced in <span style={{ color: BRAND.blueDeep, fontWeight: 600 }}>cedis</span>.
    </>
  ),
  primaryCta:   { label: 'Book a 20-minute demo',  to: 'bookDemo' },
  secondaryCta: { label: 'See how it works',       to: 'features',  anchor: true },
};

const TRUST_BADGES = [
  { icon: Globe2,    title: 'Built in Ghana',           sub: 'Cedis, our courts, our compliance' },
  { icon: Lock,      title: 'Bank-grade security',      sub: 'End-to-end encrypted messaging' },
  { icon: Shield,    title: 'Tamper-evident records',   sub: 'SHA-256 hash chain + Bitcoin anchoring' },
  { icon: Users,     title: 'Multi-tenant by design',   sub: 'Your firm\'s data, isolated by RLS' },
];

const FEATURES = [
  {
    id: 'matters',
    icon: Briefcase,
    badge: 'Case files & clients',
    title: 'Every file, every party, every deadline — in one place.',
    points: [
      'Track case files across practice areas with custom statuses',
      'Conflict-check before you take on a new file',
      'Confidential case files with strict cascade access controls',
      'Court date + deadline tracking with automatic reminders',
    ],
    accent: 'blue',
  },
  {
    id: 'billing',
    icon: DollarSign,
    badge: 'Time, billing & trust',
    title: 'Track time, bill clients, manage trust accounts — in cedis.',
    points: [
      'One-click time entries from any case file',
      'Generate invoices in GH₵ with VAT lines',
      'Trust ledger with automatic three-way reconciliation',
      'Paystack-powered online invoice payments',
    ],
    accent: 'orange',
  },
  {
    id: 'messaging',
    icon: MessageSquare,
    badge: 'Secure messaging',
    title: 'Chat with your team and your clients — without ever using WhatsApp.',
    points: [
      'End-to-end encrypted team channels (AES-256-GCM)',
      'Client portal with file sharing + e-signatures',
      'Tamper-evident message hash chain',
      'Optional Bitcoin anchoring for evidentiary integrity',
    ],
    accent: 'blue',
  },
  {
    id: 'documents',
    icon: FileText,
    badge: 'Documents & DMS',
    title: 'Documents with classification levels and version history.',
    points: [
      'Five-tier classification — Public to Confidential',
      'Per-document access control + version log',
      'Legal-hold mode for case files under litigation',
      'Client portal sharing with audit trail',
    ],
    accent: 'orange',
  },
];

const WORKFLOW = [
  { icon: Sparkles, label: 'Intake',     copy: 'New client form → contact record' },
  { icon: Scale,    label: 'Conflict',   copy: 'Auto-check against existing files' },
  { icon: Briefcase, label: 'Case file', copy: 'Open the file, assign the team' },
  { icon: Clock,    label: 'Work',       copy: 'Track time as you go' },
  { icon: Gavel,    label: 'Court',      copy: 'Deadlines + calendar in sync' },
  { icon: DollarSign, label: 'Bill',     copy: 'Invoice + collect in cedis' },
];

// Pricing teaser — prices flow from PLAN_META (src/lib/plans.js) so
// the home-page teaser, the /pricing page, the in-app upgrade modal,
// the suspended page, and the webhook PLAN_CAPACITY all stay in
// lockstep. v1.0.32 fixed this drift on PricingPage; v1.0.35 brings
// the home-page teaser into the same single-source-of-truth chain.
const PRICING = [
  {
    tier:    'Basic',
    price:   `GH₵ ${PLAN_META.basic.priceGhs}`,
    cadence: '/ firm / month',
    blurb:   'Everything a solo or small firm needs to leave spreadsheets behind.',
    features: [
      `Up to 5 lawyers`,
      `${PLAN_META.basic.storageGb} GB document storage`,
      'Case files, time, billing, trust accounting',
      'Client portal',
      'Email support',
    ],
    cta:    'Start 14-day free trial',
    ctaTo:  'signup',
    accent: 'blue',
  },
  {
    tier:    'Pro',
    price:   `GH₵ ${PLAN_META.pro.priceGhs.toLocaleString()}`,
    cadence: '/ firm / month',
    blurb:   'For growing firms — custom roles, advanced reports, calendar sync, and serious-firm compliance.',
    features: [
      // Universal Pro features first
      `Up to 10 lawyers`,
      `${PLAN_META.pro.storageGb} GB document storage`,
      'Everything in Basic',
      'Custom roles & permissions',
      'Custom rate cards per matter',
      'Document templates library',  // shipped v1.0.37
      // Roadmap signals — match the dedicated /pricing page treatment
      // so customers see the same forward-looking value pitch from
      // either entry point.
      { text: 'Google Calendar / Outlook sync', soon: true },
      // Compliance/security
      'End-to-end encrypted messaging',
      'Tamper-evident audit trail',
      'Priority support + onboarding',
    ],
    cta:       'Start 14-day free trial',
    ctaTo:     'signup',
    accent:    'orange',
    highlight: true,
  },
];

// ─── Page ───────────────────────────────────────────────────────────

export default function MarketingHomePage() {
  return (
    <main style={{ background: BRAND.bgWhite, color: BRAND.textBody }}>

      {/* ════════ HERO ════════ */}
      <Hero />

      {/* ════════ TRUST STRIP ════════ */}
      <TrustStrip />

      {/* ════════ FEATURES ════════ */}
      <section id="features" className="py-20 lg:py-28 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="What's inside"
            title="A practice-management platform, not a spreadsheet replacement."
            sub="DocketFlow replaces six tools at once — and is the only one designed for the realities of practicing law in Ghana."
          />
          <div className="mt-16 space-y-24">
            {FEATURES.map((f, i) => (
              <FeatureRow key={f.badge} feature={f} reverse={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════ WORKFLOW ════════ */}
      <WorkflowStrip />

      {/* ════════ PRICING TEASER ════════
          Now that /pricing is a dedicated page (v1.0.11), the home
          teaser is intentionally slim — two cards, no FAQ, and a
          prominent "See full pricing" CTA that drives visitors to
          the comparison table + FAQ. */}
      <section id="pricing" className="py-20 lg:py-28 scroll-mt-20" style={{ background: BRAND.bgLight }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Pricing"
            title="Simple pricing in cedis. No surprises."
            sub="One flat price per firm. Cancel anytime. We bill in GH₵ via Paystack — no FX games, no surprises."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {PRICING.map((p) => <PricingCard key={p.tier} plan={p} />)}
          </div>
          <div className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => goTo('pricing')}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-xl transition-all"
              style={{
                background: BRAND.bgWhite,
                color: BRAND.blueDeep,
                border: `1.5px solid ${BRAND.border}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = BRAND.blueUi;
                e.currentTarget.style.background = BRAND.blueGhost;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = BRAND.border;
                e.currentTarget.style.background = BRAND.bgWhite;
              }}
            >
              See full pricing &amp; FAQ
              <ArrowRight size={14} />
            </button>
            <span className="text-sm" style={{ color: BRAND.textMuted }}>
              or{' '}
              <button
                onClick={() => goTo('bookDemo')}
                className="font-semibold underline-offset-4 hover:underline"
                style={{ color: BRAND.orangeDark }}
              >
                talk to us about Enterprise →
              </button>
            </span>
          </div>
        </div>
      </section>

      {/* ════════ QUOTE / TESTIMONIAL ════════ */}
      <QuoteCard />

      {/* ════════ BIG FINAL CTA ════════ */}
      <FinalCta />

    </main>
  );
}

// ────────────────────────────────────────────────────────────────────
// HERO
// ────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section
      className="relative pt-16 lg:pt-24 pb-20 lg:pb-32 overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${BRAND.bgWhite} 0%, ${BRAND.bgLight} 100%)`,
      }}
    >
      {/* Decorative background — subtle blue radial glow */}
      <div
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 0%, ${BRAND.blueGhost} 0%, transparent 55%)`,
          opacity: 0.6,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">

          {/* Eyebrow chip */}
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-7"
            style={{
              background: BRAND.orangeSoft,
              color: BRAND.orangeDark,
              border: `1px solid ${BRAND.orangeSoft}`,
            }}
          >
            <Sparkles size={12} />
            {HERO.eyebrow}
          </div>

          {/* Headline */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6"
            style={{ color: BRAND.textHead }}
          >
            {HERO.headline}
          </h1>

          {/* Sub */}
          <p
            className="text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-10"
            style={{ color: BRAND.textMuted }}
          >
            {HERO.sub}
          </p>

          {/* CTAs — primary (Book a demo, orange) + secondary (Watch
              demo, opens the in-page modal with the animated demo). The
              "Watch demo" path is the zero-friction conversion lever:
              visitor sees the product in 30 seconds without committing
              to a scheduled call. */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => goTo(HERO.primaryCta.to)}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-xl transition-all w-full sm:w-auto"
              style={{
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
                boxShadow: '0 8px 24px rgba(249,115,22,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(249,115,22,0.45)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(249,115,22,0.35)';
              }}
            >
              {HERO.primaryCta.label}
              <ArrowRight size={16} />
            </button>
            <WatchDemoButton variant="outline" size="lg" label="Watch demo (30s)" />
          </div>

          {/* Tertiary affordance: "or sign in" small text link */}
          <p className="text-xs mt-6" style={{ color: BRAND.textFaint }}>
            Already have an account?{' '}
            <button
              onClick={() => goTo('login')}
              className="font-semibold underline-offset-4 hover:underline"
              style={{ color: BRAND.blueDeep }}
            >
              Sign in →
            </button>
          </p>
        </div>

        {/* Hero product preview — animated 6-scene demo that cycles
            through the workflow (intake → conflict → case file → time
            → invoice → paid). Auto-plays, pauses on hover. */}
        <MarketingHeroDemo />
      </div>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
// TRUST STRIP
// ────────────────────────────────────────────────────────────────────

function TrustStrip() {
  return (
    <section className="py-12" style={{ background: BRAND.bgLight, borderTop: `1px solid ${BRAND.border}`, borderBottom: `1px solid ${BRAND.border}` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {TRUST_BADGES.map((b) => {
            const Ic = b.icon;
            return (
              <div key={b.title} className="flex items-start gap-3">
                <div
                  className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
                >
                  <Ic size={18} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: BRAND.textHead }}>
                    {b.title}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: BRAND.textMuted }}>
                    {b.sub}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// FEATURES
// ────────────────────────────────────────────────────────────────────

function SectionHeader({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl mx-auto text-center">
      <p
        className="text-xs font-bold uppercase tracking-widest mb-3"
        style={{ color: BRAND.orangeDark }}
      >
        {eyebrow}
      </p>
      <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
        style={{ color: BRAND.textHead }}
      >
        {title}
      </h2>
      {sub && (
        <p className="text-base sm:text-lg mt-5 leading-relaxed" style={{ color: BRAND.textMuted }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function FeatureRow({ feature, reverse }) {
  const Ic = feature.icon;
  const isOrange = feature.accent === 'orange';
  const accentBg   = isOrange ? BRAND.orangeSoft : BRAND.blueGhost;
  const accentText = isOrange ? BRAND.orangeDark : BRAND.blueDeep;

  return (
    <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? 'lg:flex-row-reverse' : ''}`}>
      {/* Copy column */}
      <div className={`${reverse ? 'lg:order-2' : ''}`}>
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-5"
          style={{ background: accentBg, color: accentText }}
        >
          <Ic size={12} />
          {feature.badge}
        </div>
        <h3
          className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight mb-5"
          style={{ color: BRAND.textHead }}
        >
          {feature.title}
        </h3>
        <ul className="space-y-3">
          {feature.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-base" style={{ color: BRAND.textBody }}>
              <Check size={18} className="mt-0.5 shrink-0" style={{ color: accentText }} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Visual column — stylized panel that hints at the feature */}
      <div className={`${reverse ? 'lg:order-1' : ''}`}>
        <FeatureVisual feature={feature} />
      </div>
    </div>
  );
}

function FeatureVisual({ feature }) {
  // Dispatch to a custom mock per feature. Each is hand-tailored to look
  // like the actual area of the product it represents — much higher-fidelity
  // than the generic card-stack template. A real product screenshot would
  // be the gold standard, but these mocks render cleanly without needing
  // to ship + version image files, and they stay in sync when the brand
  // tokens change.
  switch (feature.id) {
    case 'matters':   return <MattersVisual />;
    case 'billing':   return <BillingVisual />;
    case 'messaging': return <MessagingVisual />;
    case 'documents': return <DocumentsVisual />;
    default:          return <MattersVisual />;
  }
}

// ── Outer card shell shared by all four feature visuals ─────────────
function VisualCard({ accentColor, title, children }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{
        background: BRAND.bgWhite,
        borderColor: BRAND.border,
        boxShadow: '0 16px 48px rgba(15,42,95,0.10), 0 4px 16px rgba(15,42,95,0.06)',
      }}
    >
      {/* Browser-style window chrome */}
      <div
        className="px-4 py-2.5 border-b flex items-center gap-2"
        style={{ background: BRAND.bgLight, borderColor: BRAND.border }}
      >
        <div className="w-2 h-2 rounded-full" style={{ background: '#FF5F57' }} />
        <div className="w-2 h-2 rounded-full" style={{ background: '#FEBC2E' }} />
        <div className="w-2 h-2 rounded-full" style={{ background: '#28C840' }} />
        <div
          className="ml-3 text-[11px] font-semibold flex items-center gap-1.5"
          style={{ color: accentColor }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor }} />
          {title}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── 1. Case files: list view ────────────────────────────────────────
// The DB table is still `matters` (component name reflects that) but
// the user-facing label in the app is "Case Files" everywhere — kept
// consistent with the sidebar nav and the AppShell rendering.
function MattersVisual() {
  const caseFiles = [
    { name: 'Mensah v. Republic',         client: 'Kwame Mensah',     area: 'Criminal',  status: 'Open',     tone: 'blue',   deadline: 'Filing tomorrow' },
    { name: 'Boateng Estate planning',    client: 'Akua Boateng',     area: 'Probate',   status: 'Open',     tone: 'blue',   deadline: 'Meeting Thu' },
    { name: 'Asante Trading Ltd v. GRA',  client: 'Asante Trading',   area: 'Tax',       status: 'In court', tone: 'orange', deadline: 'Hearing 28 May' },
    { name: 'Owusu divorce',              client: 'Ama Owusu',        area: 'Family',    status: 'Sealed',   tone: 'red',    deadline: 'Confidential' },
    { name: 'Mensah Properties Ltd',      client: 'Mensah Holdings',  area: 'Corporate', status: 'Closed',   tone: 'gray',   deadline: 'Archived' },
  ];
  const toneClass = (t) => {
    if (t === 'blue')   return { bg: BRAND.blueGhost,    fg: BRAND.blueDeep };
    if (t === 'orange') return { bg: BRAND.orangeSoft,   fg: BRAND.orangeDark };
    if (t === 'red')    return { bg: 'rgba(239,68,68,0.10)', fg: '#DC2626' };
    return { bg: BRAND.bgLighter, fg: BRAND.textMuted };
  };

  return (
    <VisualCard accentColor={BRAND.blueDeep} title="Case Files">
      <div className="p-4">
        {/* Header strip — count + filter chips */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold" style={{ color: BRAND.textHead }}>
            24 active case files
          </p>
          <div className="flex items-center gap-1.5">
            {['All', 'Open', 'Sealed'].map((f, i) => (
              <span
                key={f}
                className="text-[10px] px-2 py-0.5 rounded-full"
                style={{
                  background: i === 0 ? BRAND.blueGhost : BRAND.bgLight,
                  color:      i === 0 ? BRAND.blueDeep  : BRAND.textMuted,
                  border:    `1px solid ${i === 0 ? BRAND.blueSoft : BRAND.border}`,
                }}
              >
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Case file rows */}
        <div className="space-y-1.5">
          {caseFiles.map((m) => {
            const t = toneClass(m.tone);
            return (
              <div
                key={m.name}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border"
                style={{ borderColor: BRAND.borderSoft, background: BRAND.bgWhite }}
              >
                <div
                  className="w-7 h-7 rounded-md shrink-0 flex items-center justify-center"
                  style={{ background: t.bg, color: t.fg }}
                >
                  {m.tone === 'red' ? <FileLock2 size={13} /> : <Briefcase size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold truncate" style={{ color: BRAND.textHead }}>
                    {m.name}
                  </p>
                  <p className="text-[10px] truncate" style={{ color: BRAND.textMuted }}>
                    {m.client} · {m.area}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                    style={{ background: t.bg, color: t.fg }}
                  >
                    {m.status}
                  </span>
                  <p className="text-[9px] mt-0.5" style={{ color: BRAND.textFaint }}>
                    {m.deadline}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </VisualCard>
  );
}

// ── 2. Billing: invoice preview ─────────────────────────────────────
function BillingVisual() {
  return (
    <VisualCard accentColor={BRAND.orangeDark} title="Invoice INV-0042">
      <div className="p-5">
        {/* Invoice header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: BRAND.textFaint }}>
              Bill to
            </p>
            <p className="text-xs font-semibold mt-1" style={{ color: BRAND.textHead }}>
              Asante Trading Ltd
            </p>
            <p className="text-[10px]" style={{ color: BRAND.textMuted }}>
              East Legon, Accra
            </p>
          </div>
          <div className="text-right">
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider"
              style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
            >
              Due 28 May
            </span>
            <p className="text-[10px] mt-1" style={{ color: BRAND.textMuted }}>
              Issued 14 May 2026
            </p>
          </div>
        </div>

        {/* Line items */}
        <div className="rounded-lg overflow-hidden border" style={{ borderColor: BRAND.borderSoft }}>
          <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-1.5 text-[9px] uppercase tracking-wider font-bold"
               style={{ background: BRAND.bgLight, color: BRAND.textFaint }}>
            <span>Description</span>
            <span className="text-right">Hours</span>
            <span className="text-right">Amount</span>
          </div>
          {[
            { d: 'Drafting reply to GRA notice',          h: '4.5', a: 'GH₵ 2,250' },
            { d: 'Client conference + strategy memo',     h: '2.0', a: 'GH₵ 1,000' },
            { d: 'Filing fee — Tax Appeals Tribunal',     h: '—',   a: 'GH₵   320' },
            { d: 'Junior research (paralegal)',           h: '3.0', a: 'GH₵   450' },
          ].map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-1.5 text-[10px]"
              style={{ borderTop: `1px solid ${BRAND.borderSoft}`, background: BRAND.bgWhite, color: BRAND.textBody }}
            >
              <span className="truncate">{row.d}</span>
              <span className="text-right tabular-nums" style={{ color: BRAND.textMuted }}>{row.h}</span>
              <span className="text-right tabular-nums font-semibold">{row.a}</span>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="mt-3 space-y-1">
          {[
            { l: 'Subtotal', v: 'GH₵ 4,020.00', strong: false },
            { l: 'VAT (15%)', v: 'GH₵   603.00', strong: false },
            { l: 'NHIL (2.5%)', v: 'GH₵   100.50', strong: false },
            { l: 'Total', v: 'GH₵ 4,723.50', strong: true },
          ].map((row) => (
            <div
              key={row.l}
              className={`flex items-center justify-between text-[10px] ${row.strong ? 'pt-1.5 mt-1.5' : ''}`}
              style={{
                color: row.strong ? BRAND.textHead : BRAND.textMuted,
                fontWeight: row.strong ? 700 : 500,
                borderTop: row.strong ? `1px solid ${BRAND.border}` : 'none',
              }}
            >
              <span>{row.l}</span>
              <span className="tabular-nums">{row.v}</span>
            </div>
          ))}
        </div>

        {/* Pay button */}
        <button
          className="w-full mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg"
          style={{
            background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
            color: '#fff',
            cursor: 'default',
          }}
        >
          <Receipt size={12} />
          Pay GH₵ 4,723.50 via Paystack
        </button>
      </div>
    </VisualCard>
  );
}

// ── 3. Messaging: encrypted chat ────────────────────────────────────
function MessagingVisual() {
  const messages = [
    { from: 'Ama',     side: 'l', body: 'I\'ve uploaded the witness statements to the file. Three of them are flagged confidential — please use the secure share link only.', time: '10:42' },
    { from: 'Kwame',   side: 'r', body: 'Got it. Just downloaded. The hash on doc #2 matches what you sent yesterday — chain is intact.', time: '10:44' },
    { from: 'Ama',     side: 'l', body: '@Kwame can you draft the reply by EOD? Tribunal moved up the date.', time: '10:45', mention: true },
    { from: 'Kwame',   side: 'r', body: 'On it. Anchoring this thread to Bitcoin tonight just to be safe.', time: '10:46' },
  ];

  return (
    <VisualCard accentColor={BRAND.blueDeep} title="# asante-trading-vs-gra · 4 members">
      <div className="p-4">
        {/* Encryption notice */}
        <div
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg mb-3 text-[10px] font-semibold"
          style={{
            background: BRAND.blueGhost,
            color: BRAND.blueDeep,
          }}
        >
          <Lock size={11} />
          End-to-end encrypted · AES-256-GCM
          <span className="ml-auto text-[9px]" style={{ color: BRAND.blueDeep, opacity: 0.7 }}>
            <Hash size={9} className="inline mr-0.5" />
            Chain verified
          </span>
        </div>

        {/* Messages */}
        <div className="space-y-2">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.side === 'r' ? 'justify-end' : 'justify-start'}`}>
              <div className="max-w-[78%]">
                {m.side === 'l' && (
                  <p className="text-[9px] mb-0.5 ml-2 font-semibold" style={{ color: BRAND.textMuted }}>
                    {m.from}
                  </p>
                )}
                <div
                  className="px-3 py-1.5 rounded-2xl"
                  style={{
                    background: m.side === 'r' ? BRAND.blueDeep : BRAND.bgLight,
                    color:      m.side === 'r' ? '#fff' : BRAND.textBody,
                    borderTopLeftRadius:  m.side === 'l' ? '6px' : undefined,
                    borderTopRightRadius: m.side === 'r' ? '6px' : undefined,
                  }}
                >
                  <p className="text-[10px] leading-snug">
                    {m.mention && (
                      <span
                        className="font-semibold mr-1"
                        style={{ color: m.side === 'r' ? '#FFD7B5' : BRAND.orangeDark }}
                      >
                        @Kwame
                      </span>
                    )}
                    {m.mention ? m.body.replace('@Kwame ', '') : m.body}
                  </p>
                  <p
                    className="text-[8px] mt-0.5 text-right"
                    style={{ color: m.side === 'r' ? 'rgba(255,255,255,0.5)' : BRAND.textFaint }}
                  >
                    {m.time}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Input mock */}
        <div
          className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl border"
          style={{ borderColor: BRAND.border, background: BRAND.bgWhite }}
        >
          <AtSign size={12} style={{ color: BRAND.textFaint }} />
          <span className="text-[10px] flex-1" style={{ color: BRAND.textFaint }}>
            Type a message…
          </span>
          <Paperclip size={11} style={{ color: BRAND.textFaint }} />
          <div
            className="w-6 h-6 rounded-md flex items-center justify-center"
            style={{ background: BRAND.blueDeep, color: '#fff' }}
          >
            <Send size={10} />
          </div>
        </div>
      </div>
    </VisualCard>
  );
}

// ── 4. Documents: file tree with classification ─────────────────────
function DocumentsVisual() {
  const folders = [
    { name: 'Pleadings',    files: 12, open: true },
    { name: 'Evidence',     files: 8,  open: true, items: [
      { name: 'Witness statement — A. Owusu.pdf',  classification: 'Confidential', tone: 'red' },
      { name: 'Contract dated 2024-11-03.pdf',     classification: 'Internal',     tone: 'blue' },
      { name: 'Email thread — GRA correspondence', classification: 'Restricted',   tone: 'orange' },
    ]},
    { name: 'Court filings', files: 5,  open: false },
    { name: 'Correspondence', files: 23, open: false },
  ];
  const toneClass = (t) => {
    if (t === 'red')    return { bg: 'rgba(239,68,68,0.10)', fg: '#DC2626' };
    if (t === 'orange') return { bg: BRAND.orangeSoft,        fg: BRAND.orangeDark };
    if (t === 'blue')   return { bg: BRAND.blueGhost,         fg: BRAND.blueDeep };
    return { bg: BRAND.bgLighter, fg: BRAND.textMuted };
  };

  return (
    <VisualCard accentColor={BRAND.orangeDark} title="Asante Trading Ltd — Documents">
      <div className="p-4">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold" style={{ color: BRAND.textHead }}>
            48 files · 3 folders · Legal hold active
          </p>
          <div
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider"
            style={{ background: 'rgba(239,68,68,0.10)', color: '#DC2626' }}
          >
            <ShieldCheck size={9} />
            Hold
          </div>
        </div>

        {/* File tree */}
        <div className="space-y-1">
          {folders.map((f) => (
            <div key={f.name}>
              <div
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px]"
                style={{ background: f.open ? BRAND.bgLight : 'transparent', color: BRAND.textHead }}
              >
                <ChevRight
                  size={10}
                  style={{
                    transform: f.open ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.2s',
                    color: BRAND.textMuted,
                  }}
                />
                <Folder size={12} style={{ color: BRAND.blueDeep }} />
                <span className="font-semibold">{f.name}</span>
                <span className="ml-auto text-[10px]" style={{ color: BRAND.textFaint }}>{f.files}</span>
              </div>
              {f.open && f.items && (
                <div className="ml-6 space-y-1 mt-1">
                  {f.items.map((item) => {
                    const t = toneClass(item.tone);
                    return (
                      <div
                        key={item.name}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px]"
                        style={{ background: BRAND.bgWhite, border: `1px solid ${BRAND.borderSoft}` }}
                      >
                        <FilePlus size={11} style={{ color: BRAND.textMuted }} />
                        <span className="flex-1 truncate" style={{ color: BRAND.textBody }}>
                          {item.name}
                        </span>
                        <span
                          className="text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                          style={{ background: t.bg, color: t.fg }}
                        >
                          {item.classification}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Audit trail footer */}
        <div
          className="mt-3 pt-3 flex items-center gap-2 text-[9px] border-t"
          style={{ borderColor: BRAND.borderSoft, color: BRAND.textMuted }}
        >
          <AlertCircle size={9} />
          Every download is logged · Audit trail available
        </div>
      </div>
    </VisualCard>
  );
}

// ────────────────────────────────────────────────────────────────────
// WORKFLOW
// ────────────────────────────────────────────────────────────────────

function WorkflowStrip() {
  return (
    <section className="py-20" style={{ background: BRAND.blueDeep, color: '#fff' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.orange }}>
            One workflow, end to end
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            From intake to invoice — without leaving DocketFlow.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-2">
          {WORKFLOW.map((w, i) => {
            const Ic = w.icon;
            return (
              <div key={w.label} className="relative">
                <div className="flex flex-col items-center text-center px-3 py-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      background: 'rgba(249,115,22,0.18)',
                      color: BRAND.orange,
                    }}
                  >
                    <Ic size={20} />
                  </div>
                  <p className="text-sm font-bold mb-1">{w.label}</p>
                  <p className="text-xs leading-snug" style={{ color: 'rgba(226,232,240,0.7)' }}>
                    {w.copy}
                  </p>
                </div>
                {/* Connector — hidden on mobile + last item */}
                {i < WORKFLOW.length - 1 && (
                  <div
                    aria-hidden
                    className="hidden lg:block absolute top-11 -right-1 w-2"
                    style={{ color: BRAND.orange }}
                  >
                    <ArrowRight size={14} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// PRICING CARD
// ────────────────────────────────────────────────────────────────────

function PricingCard({ plan }) {
  const isOrange = plan.accent === 'orange';
  return (
    <div
      className="relative rounded-2xl p-7 lg:p-8 border-2 transition-transform"
      style={{
        background: BRAND.bgWhite,
        borderColor: plan.highlight ? BRAND.orange : BRAND.border,
        boxShadow: plan.highlight
          ? '0 24px 64px rgba(249,115,22,0.18), 0 8px 24px rgba(15,42,95,0.08)'
          : '0 8px 32px rgba(15,42,95,0.06)',
      }}
    >
      {plan.highlight && (
        <div
          className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
            color: '#fff',
          }}
        >
          Recommended
        </div>
      )}

      <p className="text-sm font-semibold mb-2" style={{ color: isOrange ? BRAND.orangeDark : BRAND.blueDeep }}>
        {plan.tier}
      </p>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-4xl font-bold" style={{ color: BRAND.textHead }}>
          {plan.price}
        </span>
        <span className="text-sm" style={{ color: BRAND.textMuted }}>
          {plan.cadence}
        </span>
      </div>
      <p className="text-sm mb-6 leading-relaxed" style={{ color: BRAND.textMuted }}>
        {plan.blurb}
      </p>

      <ul className="space-y-2.5 mb-7">
        {plan.features.map((f) => {
          // Feature items can be either a plain string OR
          // { text, soon } — the latter renders a "Coming soon" pill
          // (matches the dedicated /pricing page treatment).
          const text = typeof f === 'string' ? f : f.text;
          const soon = typeof f === 'object' && f.soon;
          return (
            <li key={text} className="flex items-start gap-2.5 text-sm" style={{ color: soon ? BRAND.textMuted : BRAND.textBody }}>
              <Check size={16} className="mt-0.5 shrink-0" style={{ color: soon ? BRAND.textFaint : (isOrange ? BRAND.orangeDark : BRAND.blueDeep) }} />
              <span className="flex-1">
                {text}
                {soon && (
                  <span
                    className="ml-2 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-baseline"
                    style={{ background: '#fef3c7', color: '#92400e' }}
                  >
                    Coming soon
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <button
        onClick={() => goTo(plan.ctaTo)}
        className="w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold rounded-xl transition-all"
        style={
          plan.highlight
            ? {
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
                boxShadow: '0 6px 20px rgba(249,115,22,0.30)',
              }
            : {
                background: BRAND.blueDeep,
                color: '#fff',
              }
        }
      >
        {plan.cta}
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// QUOTE / TESTIMONIAL
// ────────────────────────────────────────────────────────────────────

function QuoteCard() {
  // Single intentional placeholder — we don't ship fabricated quotes.
  // Once a real customer agrees to be quoted, swap in their words +
  // name + firm + photo.
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="rounded-3xl p-8 lg:p-12 border relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${BRAND.bgWhite} 0%, ${BRAND.bgLight} 100%)`,
            borderColor: BRAND.border,
            boxShadow: '0 16px 56px rgba(15,42,95,0.08)',
          }}
        >
          <BookOpen
            size={120}
            className="absolute -top-6 -right-6 opacity-[0.04]"
            style={{ color: BRAND.blueDeep }}
          />
          <div className="relative">
            <p className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: BRAND.orangeDark }}>
              Why we built this
            </p>
            <p
              className="text-xl sm:text-2xl leading-relaxed mb-6"
              style={{ color: BRAND.textHead, fontStyle: 'italic' }}
            >
              "Practice-management software shouldn't require a US-priced subscription
              and a JD in Microsoft Excel to use. It should track your case files, bill your
              clients in cedis, and stay out of the way the rest of the time."
            </p>
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: BRAND.blueDeep, color: '#fff' }}
              >
                <Scale size={18} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: BRAND.textHead }}>
                  The DocketFlow team
                </p>
                <p className="text-xs" style={{ color: BRAND.textMuted }}>
                  Accra, Ghana
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────
// FINAL CTA
// ────────────────────────────────────────────────────────────────────

function FinalCta() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="rounded-3xl p-10 lg:p-14 text-center"
          style={{
            background: `linear-gradient(135deg, ${BRAND.blueDeep} 0%, ${BRAND.blueMid} 100%)`,
            color: '#fff',
            boxShadow: '0 24px 64px rgba(15,42,95,0.30)',
          }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5">
            See DocketFlow in 20 minutes.
          </h2>
          <p
            className="text-base sm:text-lg max-w-xl mx-auto mb-9 leading-relaxed"
            style={{ color: 'rgba(226,232,240,0.85)' }}
          >
            We'll show you the parts that matter for your practice, answer your questions,
            and help you map your current workflow onto DocketFlow.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => goTo('bookDemo')}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold rounded-xl transition-all w-full sm:w-auto"
              style={{
                background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                color: '#fff',
                boxShadow: '0 8px 24px rgba(249,115,22,0.40)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Book a demo
              <ArrowRight size={16} />
            </button>
            {/* Watch-demo is the secondary path here — visitors who
                aren't ready to schedule can preview the product in 30s
                without commitment. */}
            <WatchDemoButton variant="ghost" size="lg" label="Watch demo (30s)" />
          </div>
        </div>
      </div>
    </section>
  );
}
